import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/i18n/LocaleProvider";
import {
  sendChatbotMessage,
  type ChatbotMessageRequest,
  type ChatbotPageContext,
} from "@/services/chatbot.service";

export interface ChatSessionMessage {
  id: string;
  role: "user" | "bot";
  content: string;
  isError: boolean;
  timestamp: Date;
  /** Optional Tailwind classes for the bubble — used for welcome/synthetic messages that want consistent sizing. */
  bubbleClassName?: string;
}

export interface ChatSessionContext {
  page_context: ChatbotPageContext;
  page_path?: string;
}

export interface UseChatSessionResult {
  messages: ChatSessionMessage[];
  isTyping: boolean;
  sendMessage: (text: string) => Promise<void>;
  sessionId: string;
  clearMessages: () => void;
}

type ContextProvider = () => ChatSessionContext | undefined | null;

function createMessage(
  role: "user" | "bot",
  content: string,
  options: { isError?: boolean } = {},
): ChatSessionMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    isError: options.isError ?? false,
    timestamp: new Date(),
  };
}

function extractBackendReply(
  responseData: { reply?: string; session_id?: string } | undefined,
  fallbackReply: string,
): {
  content: string;
  sessionId: string | undefined;
} {
  const reply = responseData?.reply;
  return {
    content: typeof reply === "string" && reply.trim() ? reply : fallbackReply,
    sessionId: responseData?.session_id,
  };
}

export function useChatSession(
  apiUrl?: string,
  contextProvider?: ContextProvider,
): UseChatSessionResult {
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const [messages, setMessages] = useState<ChatSessionMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  // Platform locale ("en" | "fr") drives what language the Dialogflow agent
  // answers in and which language the synthetic/error replies render in.
  const { locale } = useLocale();
  const { t } = useTranslation("chatbot");

  const urlOverride = typeof apiUrl === "string" && apiUrl.trim() ? apiUrl.trim() : undefined;
  const contextProviderRef = useRef<ContextProvider | undefined>(contextProvider);
  contextProviderRef.current = contextProvider;

  // A Dialogflow session is bound to the language it was opened with. When the
  // user switches the platform language mid-conversation, start a fresh session
  // (and clear the transcript) so the next turn isn't a language mismatch.
  const prevLocaleRef = useRef(locale);
  useEffect(() => {
    if (prevLocaleRef.current !== locale) {
      prevLocaleRef.current = locale;
      setMessages([]);
      sessionIdRef.current = crypto.randomUUID();
    }
  }, [locale]);

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed || isTyping) return;

      setMessages((prev) => [...prev, createMessage("user", trimmed)]);
      setIsTyping(true);

      try {
        const body: ChatbotMessageRequest = {
          message: trimmed,
          session_id: sessionIdRef.current,
          // Sync the bot to the live platform locale so Emma answers in the
          // same language as the UI (the backend normalizes en/fr).
          language_code: locale,
        };

        const ctx = contextProviderRef.current?.();
        if (ctx?.page_context) body.page_context = ctx.page_context;
        if (ctx?.page_path) body.page_path = ctx.page_path;

        const result = await sendChatbotMessage(body, urlOverride);

        if (result.error || !result.data) {
          setMessages((prev) => [
            ...prev,
            createMessage("bot", result.error ?? t("replies.error"), { isError: true }),
          ]);
          return;
        }

        const reply = extractBackendReply(result.data, t("replies.fallback"));
        if (typeof reply.sessionId === "string" && reply.sessionId.trim()) {
          sessionIdRef.current = reply.sessionId;
        }
        setMessages((prev) => [...prev, createMessage("bot", reply.content)]);
      } catch {
        setMessages((prev) => [...prev, createMessage("bot", t("replies.error"), { isError: true })]);
      } finally {
        setIsTyping(false);
      }
    },
    [isTyping, urlOverride, locale, t],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    sessionIdRef.current = crypto.randomUUID();
  }, []);

  return {
    messages,
    isTyping,
    sendMessage,
    sessionId: sessionIdRef.current,
    clearMessages,
  };
}
